<script lang="ts">
  import { getContext } from 'svelte';
  import type { Video } from '$lib/sanity/schemas';
  import { type SanityContext, key } from '$lib/context/sanity';
  import type { SignedVideoRegistry } from '$lib/mux';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import '@mux/mux-player';

  interface Props {
    document: Video;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    preload?: 'auto' | 'metadata' | 'none';
    minResolution?: '480p' | '540p' | '720p' | '1080p' | '1440p' | '2160p';
    renditionOrder?: 'desc';
  }

  let { document, sourceMap, path = [], preload, minResolution, renditionOrder }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);

  let imageUrl = $derived(
    document.poster && urlBuilder.image(document.poster.asset).auto('format').url()
  );
  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const signedVideoRegistry = getContext<SignedVideoRegistry>('signedVideos');
  const signedVideo =
    document.variant === 'muxVideo' && document.muxVideo && signedVideoRegistry
      ? signedVideoRegistry[document.muxVideo._id]
      : null;
</script>

{#if signedVideo}
  <mux-player
    {preload}
    stream-type="on-demand"
    playback-id={signedVideo.playbackId}
    playback-token={signedVideo.tokens.playback}
    thumbnail-token={signedVideo.tokens.thumbnail}
    storyboard-token={signedVideo.tokens.storyboard}
    metadata-video-title={document.title}
    autoplay={document.muxOptions.autoplay}
    muted={document.muxOptions.muted}
    loop={document.muxOptions.loop}
    class:hide-controls={!document.muxOptions.showControls}
    poster={imageUrl}
    aria-label={document.description}
    style={`--mux-aspect-ratio: ${signedVideo.meta.aspectRatio}`}
    data-sanity={encodeDataAttribute([...path, 'video', '_id'])}
    min-resolution={minResolution}
    rendition-order={renditionOrder}
  ></mux-player>
{:else if document.variant === 'youtubeEmbed'}
  <iframe
    src={`https://www.youtube.com/embed/${document.youtubeID}?controls=${
      document.youtubeOptions.showControls ? 1 : 0
    }`}
    title={document.title}
    frameborder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowfullscreen
    data-sanity={encodeDataAttribute([...path, 'video', '_id'])}
  ></iframe>
{/if}

<style>
  iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  mux-player {
    width: 100%;
    aspect-ratio: var(--mux-aspect-ratio);

    &.hide-controls {
      --controls: none;
    }
  }

  mux-player.hide-controls::part(gesture-layer) {
    display: none;
  }
</style>
